import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { ProjectOnList, ProjectSortOptions } from '@/api/v4/projects.api';
import { ListStatusTag } from '@/components/list-status-tag/list-status-tag';
import { paths } from '@/routes/helpers/paths';
import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import {
  TablePrimaryCell,
  TableSecondaryCell,
} from '@/ui/table/infinite-table/table-cells';
import type { Row } from '@/ui/table/table.types';

import { useProjectListingsSectionParams } from '../project-listings/use-project-listings-section-params';

import { projectsHeaders } from './projects-table-configuration';
import { styled } from 'goober';
import { ProjectTypeTag } from './project-type-tag';
import { Button } from '@/ui/button/button';
import { StatusIndicatorCell } from './components/status-indicator-cell';
import type { ProjectStatus } from '@/api/v4/project-enrich.api';
import { ProjectCreatedForCell } from './components/project-created-for-cell';
import { ProjectLastModifiedCell } from './components/project-last-modified-cell';
import { ProjectOwnerCell } from './components/project-owner-cell';
import { colors } from '@/theme/colors';
import { TagsPreview } from '@/features/tags/tags-preview';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';

const rowPadding = {
  left: '18px',
  right: '18px',
};

const getResponsiveTableConfig = (width: number) => ({
  maxVisibleTags: width <= 1800 ? 2 : 3,
  primaryCellMaxWidth: width <= 1800 ? 140 : 300,
});

const handleResponsiveResize =
  (
    setConfig: React.Dispatch<
      React.SetStateAction<{
        maxVisibleTags: number;
        primaryCellMaxWidth: number;
      }>
    >,
  ) =>
  () => {
    const width = window.innerWidth;
    setConfig(getResponsiveTableConfig(width));
  };

const useResponsiveTableConfig = () => {
  const [config, setConfig] = useState(
    getResponsiveTableConfig(window.innerWidth),
  );

  useEffect(() => {
    const resizeHandler = handleResponsiveResize(setConfig);
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  return config;
};

export const useProjectsTableConfiguration = (projects: ProjectOnList[]) => {
  const { t } = useTranslation('projects');
  const navigateTo = useNavigateTo();
  const projectPageSearchParams = useProjectListingsSectionParams();
  const { maxVisibleTags, primaryCellMaxWidth } = useResponsiveTableConfig();

  const header = useTranslateHeaders<ProjectSortOptions>(
    projectsHeaders,
    'projects',
    'projects',
  );

  const rows: Row[] = useMemo(
    () =>
      projects.map(project => {
        return {
          id: project.id,
          rowHover: true,
          rowBackground: colors.gray.c1,
          cells: [
            {
              value: (
                <>
                  <ProjectTitleLink>
                    <TablePrimaryCell maxWidth={primaryCellMaxWidth}>
                      <EllipsisTextTooltip
                        text={project.name}
                        Component={
                          <Link
                            to={{
                              pathname: paths.project({
                                projectId: project.id,
                              }),
                              search: projectPageSearchParams,
                            }}
                          />
                        }
                      />
                    </TablePrimaryCell>
                  </ProjectTitleLink>
                  {project.listingCount > 0 ? (
                    <TableSecondaryCell>
                      <Link
                        to={{
                          pathname: paths.project({ projectId: project.id }),
                          search: projectPageSearchParams,
                        }}
                      >
                        {t('projects.company', {
                          count: project.listingCount,
                        })}
                      </Link>
                    </TableSecondaryCell>
                  ) : (
                    <TableSecondaryCell>-</TableSecondaryCell>
                  )}
                </>
              ),
            },
            {
              value: <ProjectTypeTag text={project.type} />,
            },
            {
              value: project.mostAdvancedListingStatus ? (
                <ListStatusTag text={project.mostAdvancedListingStatus} />
              ) : (
                <TableSecondaryCell>-</TableSecondaryCell>
              ),
            },
            {
              value: (
                <TagsPreview
                  tags={project.tags}
                  baseLinkUrl={paths.projects()}
                  maxVisibleTags={maxVisibleTags}
                />
              ),
            },
            {
              value: (
                <ProjectCreatedForCell createdFor={project.createdForTeam} />
              ),
            },
            {
              value: (
                <StatusIndicatorCell status={project.status as ProjectStatus} />
              ),
            },
            {
              value: (
                <ProjectLastModifiedCell
                  lastEditedDate={project.lastEditedDate}
                />
              ),
            },
            {
              value: <ProjectOwnerCell owner={project.owner} />,
            },
            {
              value: (
                <Button
                  onClick={() =>
                    navigateTo.project(
                      { projectId: project.id },
                      projectPageSearchParams,
                    )
                  }
                >{t`viewProject`}</Button>
              ),
              hoverOnly: true,
            },
          ],
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, maxVisibleTags, primaryCellMaxWidth],
  );

  return {
    header,
    rows,
    skeletonConfig: {
      numberOfRows: 7,
      rowPadding,
    },
  };
};

const ProjectTitleLink = styled('div')`
  font-weight: 600;
`;
